import { useMemo, useState } from 'react';
import { Box, CircularProgress, Collapse, Stack, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TagResponse } from 'src/types/tags/tags';
import { useUsageCategories } from 'src/features/sites/hooks';
import { SiteConfigurationTemplate, TagMapping, TemplateGateway, TemplateTag } from '../../types';
import { isForwardedInTag } from '../../utils/isForwardedInTag';
import { RegularTagRow } from './RegularTagRow';
import { ForwardedTagRow } from './ForwardedTagRow';

// Per-gateway tag breakdown

type GatewayTagsInfo = {
  gateway: TemplateGateway;
  regularTags: [string, TemplateTag][];
  forwardedTags: [string, TemplateTag][];
  totalRegular: number;
  mapped: number;
};

/** Splits each gateway's tags into regular/forwarded in a single pass, tracking how many
 * regular tags are mapped along the way — avoids re-iterating tags per gateway. */
const buildGatewayTagsInfo = (
  gateways: TemplateGateway[],
  tagMapping: TagMapping
): GatewayTagsInfo[] =>
  gateways.map((gateway) => {
    const regularTags: [string, TemplateTag][] = [];
    const forwardedTags: [string, TemplateTag][] = [];
    let mapped = 0;

    for (const entry of Object.entries(gateway.tags ?? {})) {
      const [, tag] = entry;
      if (isForwardedInTag(tag)) {
        forwardedTags.push(entry);
      } else {
        regularTags.push(entry);
        if (tagMapping[tag.idempotencyKey]) mapped += 1;
      }
    }

    return { gateway, regularTags, forwardedTags, totalRegular: regularTags.length, mapped };
  });

// Column layout

// Narrower source-tag column, fixed-width relates-to column, larger tag-name column
export const GRID = 'minmax(0, 0.8fr) minmax(0, 0.9fr) minmax(0, 1.3fr)';

// Props

type MapTagsStepProps = {
  template: SiteConfigurationTemplate;
  destinationTags: TagResponse[];
  isLoadingDestinationTags: boolean;
  tagMapping: TagMapping;
  onTagMappingChange: (sourceTagId: string, destinationTagId: string) => void;
};

// Main component

export const MapTagsStep = ({
  template,
  destinationTags,
  isLoadingDestinationTags,
  tagMapping,
  onTagMappingChange,
}: MapTagsStepProps) => {
  const theme = useTheme();
  const usageCategories = useUsageCategories(template);

  const { gateways } = template;

  const [expandedGateways, setExpandedGateways] = useState<Set<string>>(
    () => new Set(gateways.map((gw) => gw.idempotencyKey))
  );

  const toggleGateway = (gatewayId: string) => {
    setExpandedGateways((prev) => {
      const next = new Set(prev);
      if (next.has(gatewayId)) next.delete(gatewayId);
      else next.add(gatewayId);
      return next;
    });
  };

  const gatewayTagsInfo = useMemo(
    () => buildGatewayTagsInfo(gateways, tagMapping),
    [gateways, tagMapping]
  );

  // Count only regular (non-forwarded) tags for the mapping progress indicator
  const { totalRegularTags, mappedCount } = useMemo(
    () =>
      gatewayTagsInfo.reduce(
        (acc, info) => ({
          totalRegularTags: acc.totalRegularTags + info.totalRegular,
          mappedCount: acc.mappedCount + info.mapped,
        }),
        { totalRegularTags: 0, mappedCount: 0 }
      ),
    [gatewayTagsInfo]
  );

  if (isLoadingDestinationTags) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading destination tags…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Map each source tag to a tag on this site. Tags with matching names are auto-selected.
        Unmapped tags will be skipped during import.
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {mappedCount} of {totalRegularTags} tags mapped
      </Typography>

      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: 1,
          px: 1,
          pb: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          TEMPLATE TAG NAME
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          RELATES TO
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          TAG NAME
        </Typography>
      </Box>

      <Stack spacing={0} sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
        {gatewayTagsInfo.map(({ gateway, regularTags, forwardedTags }) => {
          const isExpanded = expandedGateways.has(gateway.idempotencyKey);

          return (
            <Box key={gateway.idempotencyKey} sx={{ mb: 0.5 }}>
              {/* Collapsible gateway header */}
              <Box
                onClick={() => toggleGateway(gateway.idempotencyKey)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.75,
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: theme.palette.action.selected },
                }}
              >
                {isExpanded ? (
                  <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                ) : (
                  <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                )}
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                  {gateway.name}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  {regularTags.length} tag{regularTags.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              <Collapse in={isExpanded}>
                {/* Regular tags */}
                {regularTags.map(([rawInputName, tag]) => (
                  <RegularTagRow
                    key={tag.idempotencyKey}
                    rawInputName={rawInputName}
                    tag={tag}
                    categories={usageCategories[tag.idempotencyKey] ?? []}
                    destinationTags={destinationTags}
                    tagMapping={tagMapping}
                    onTagMappingChange={onTagMappingChange}
                    theme={theme}
                  />
                ))}

                {/* Forwarded tags */}
                {forwardedTags.length > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 1, pt: 1, pb: 0.25, display: 'block', fontStyle: 'italic' }}
                    >
                      Forwarded tags
                    </Typography>
                    {forwardedTags.map(([rawInputName, tag]) => (
                      <ForwardedTagRow
                        key={tag.idempotencyKey}
                        rawInputName={rawInputName}
                        tag={tag}
                        theme={theme}
                      />
                    ))}
                  </>
                )}
              </Collapse>
            </Box>
          );
        })}

        {gateways.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No tags found in template.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
